import { createSlice, PayloadAction } from "@reduxjs/toolkit"

interface ToggledMarker {
    lat: number;
    lng: number;
}

interface IBusinessInfo {
    name: string;
    address: string;
}

interface IAdminManagerData {
    adminUser: any;
    adminBusiness: any;
}

interface Feature {
    title: string;
    subFeatures: string[];
}

export interface Plan {
    planId: string;
    name: string;
    monthly_price: number;
    yearly_price: number;
    currency: string;
    category: string;
    credits: number;
    gbpConnections: number;
    citationAllowed: boolean;
    features: Feature[];
    [key: string]: any;
}

export interface AddonPlan {
    addon_id: string;
    name: string;
    price: number;
    currency: string;
    category: string;
    features: Feature[];
    [key: string]: any;
    disabled: boolean;
    priceId: string;
    reseller_cost_cents?: number;
    reseller_product_id?: string;
    trial_days?: number;
    require_payment_upfront?: boolean;
}

export interface ExportDataItem {
    business_name: string;
    keyword: string;
    "Last Scan Date": string;
    "Avg. rank": number;
    longitude: number;
    latitude: number;
    address: string;
    "Crawl frequency (days)": number;
    pin_count: number;
    spacing: number;
    good_pins: number;
    medium_pins: number;
    bad_pins: number;
    not_ranking_pins: number;
    marketShare: number | string;
    reviews: number;
    rating: number;
    google_url: string;
    "Place id": string;
    website_url: string;
}

interface IIntialState {
    theme: string;
    showsidebar: boolean;
    toggledMarkers: ToggledMarker[];
    AllBusinessData: [];
    credits: number | null;
    originalCredits: number | null;
    status: "active" | "inactive" | "expired" | string | null;
    freeScans: number | null;
    freeUser: boolean;
    planName: string | null;
    planType: string | null;
    expiryDate: string | null;
    userEmail: string | null;
    planPrice: number | null;
    purchaseDate: string | null;
    subscriptionId: string | null;
    businessInfo: IBusinessInfo;
    selectedHistory: [];
    // isCompareModalOpen: any;
    isCompareModalOpen: {
        isModalOpen: boolean;
        isUserReport: boolean;
        mapHeatmapId: string | null;  // Add this
    };
    isEditHeatmapModalOpen: any;
    allHistoryData: [];
    billingPlans: Plan[];
    exportData: ExportDataItem[];
    isAddCreditModalOpen: boolean;
    isAddAiCreditModalOpen: boolean;
    aiCreditBusiness: any;
    isManagerHeatmapModal: boolean;
    editPlan: Plan | null;
    editAddonPlan: AddonPlan | null;
    // adminManagerIds: IAdminManagerIds;
    adminManagerData: IAdminManagerData;
    addGBPBusiness: any;
    isBusinessCompareModalOpen: boolean;
    mapAllMarkers: {
        enablePin: any[],
        disablePin: any[]
    },
    selectedBusinessId: any,
}

const initialState: IIntialState = {
    theme: 'light',
    showsidebar: false,
    toggledMarkers: [],
    AllBusinessData: [],
    credits: null,
    originalCredits: null,
    status: null,
    freeScans: null,
    freeUser: false,
    planName: null,
    planType: null,
    expiryDate: null,
    userEmail: null,
    planPrice: null,
    purchaseDate: null,
    subscriptionId: null,
    businessInfo: {
        name: '',
        address: ''
    },
    selectedHistory: [],
    isCompareModalOpen: {
        isModalOpen: false,
        isUserReport: false,
        mapHeatmapId: null
    },
    isEditHeatmapModalOpen: { isOpen: false, reportId: 0, status: false },
    allHistoryData: [],
    billingPlans: [],
    exportData: [],
    isAddCreditModalOpen: false,
    aiCreditBusiness: [],
    isAddAiCreditModalOpen: false,
    isManagerHeatmapModal: false,
    editPlan: null,
    editAddonPlan: null,
    // adminManagerIds: {
    //     userId: '',
    //     businessId: ''
    // },
    adminManagerData: {
        adminUser: null,
        adminBusiness: null
    },
    addGBPBusiness: [],
    isBusinessCompareModalOpen: false,
    mapAllMarkers: {
        enablePin: [],
        disablePin: []
    },
    selectedBusinessId: null as string | null,
}

export const MRSlice = createSlice({
    name: 'MRState',
    initialState,
    reducers: {
        changeShowSidebar: (state, action: PayloadAction<boolean>) => {
            state.showsidebar = action.payload;
        },
        changeTheme: (state) => {
            state.theme = state.theme === 'light' ? 'dark' : 'light';
        },
        toggleMarker: (state, action) => {
            const { lat, lng } = action.payload;
            const index = state.toggledMarkers.findIndex(
                (m) => m.lat === lat && m.lng === lng
            );

            if (index > -1) {
                state.toggledMarkers.splice(index, 1);
            } else {
                // Not toggled, so add
                state.toggledMarkers.push({
                    lat, lng
                });
            }
        },
        setMapAllMerkers: (state, action: PayloadAction<any>) => {
            state.mapAllMarkers = action.payload;
        },
        setMapToggleEditPin: (state, action) => {
            const { lat, lng } = action.payload;

            const index = state.mapAllMarkers.disablePin.findIndex(
                (m) => m.lat === lat && m.lng === lng
            );

            if (index !== -1) {
                state.mapAllMarkers.disablePin.splice(index, 1);
            } else {
                state.mapAllMarkers.disablePin.push({ lat, lng });
            }
        },

        resetToggledMarkers: (state) => {
            state.toggledMarkers = [];
        },
        changeAllBusinesses: (state, action: PayloadAction<any>) => {
            typeof action.payload === "function"
                ? state.AllBusinessData = (action.payload as (prev: any) => any)(state.AllBusinessData)
                : state.AllBusinessData = action.payload
        },
        setBusinessInfo: (state, action: PayloadAction<{ name: string; address: string }>) => {
            state.businessInfo = action.payload;
        },
        setSelectedHistory: (state, action: PayloadAction<any>) => {
            state.selectedHistory = action.payload;
        },
        setIsCompareModalOpen: (state, action: PayloadAction<{ isModalOpen: boolean; isUserReport: boolean; mapHeatmapId?: string | null; }>) => {
            state.isCompareModalOpen = {
                isModalOpen: action.payload.isModalOpen,
                isUserReport: action.payload.isUserReport,
                mapHeatmapId: action.payload.mapHeatmapId ?? state.isCompareModalOpen.mapHeatmapId
            };

        },
        setIsEditHeatmapModalOpen: (state, action: PayloadAction<any>) => {
            state.isEditHeatmapModalOpen = action.payload;
        },
        setHistoryData: (state, action: PayloadAction<any>) => {
            state.allHistoryData = action.payload;
        },
        setIsAddCreditModalOpen: (state, action: PayloadAction<any>) => {
            state.isAddCreditModalOpen = action.payload;
        },
        setIsAddAiCreditModalOpen: (state, action: PayloadAction<any>) => {
            state.isAddAiCreditModalOpen = action.payload.open;
            state.aiCreditBusiness = action.payload.selected;
        },
        setManagerHeatmapModal: (state, action: PayloadAction<boolean>) => {
            state.isManagerHeatmapModal = action.payload;
        },
        setEditedPlan: (state, action: PayloadAction<Plan>) => {
            state.editPlan = action.payload;
        },
        setEditedAddonPlan: (state, action: PayloadAction<AddonPlan>) => {
            state.editAddonPlan = action.payload;
        },
        // setAdminManagerIds: (state, action: PayloadAction<{ userId: string; businessId: string }>) => {
        //     state.adminManagerIds = action.payload;
        // },
        setAdminManagerIds: (state, action) => {
            state.adminManagerData = action.payload;
        },
        setAddGBPBusiness: (state, action: PayloadAction<any>) => {
            state.addGBPBusiness = action.payload;
        },
        setIsBusinessCompareModalOpen: (state, action: PayloadAction<any>) => {
            state.isBusinessCompareModalOpen = action.payload;
        },

        restDisableAllMarkers: (state) => {
            state.mapAllMarkers = {
                ...state.mapAllMarkers,
                disablePin: []
            }
        },
        setSelectedBusinessId: (state, action: PayloadAction<string | null>) => {
            state.selectedBusinessId = action.payload;
        },
    },
    extraReducers: (builder) => { },
})

export const { changeShowSidebar, changeTheme, resetToggledMarkers, toggleMarker, setMapToggleEditPin, changeAllBusinesses, setBusinessInfo, setSelectedHistory, setIsCompareModalOpen, setIsEditHeatmapModalOpen, setHistoryData, setIsAddCreditModalOpen, setIsAddAiCreditModalOpen, setManagerHeatmapModal, setEditedPlan, setEditedAddonPlan, setAdminManagerIds, setAddGBPBusiness, setIsBusinessCompareModalOpen, setMapAllMerkers, restDisableAllMarkers, setSelectedBusinessId } = MRSlice.actions

export default MRSlice.reducer